/**
 * SMPL → Mixamo pose retargeting (forward-kinematic).
 *
 * Turns SMPL pose parameters (the 72-float axis-angle representation used
 * by every academic human-pose paper since 2015 — AMASS, PoseScript,
 * BABEL, ChatPose, UniPose, …) into a `BonePose` record on our Mixamo-
 * skeleton character. The result is suitable to drop straight into
 * `PROCEDURAL_HOLD_POSES` or to live-preview as an additive hold.
 *
 * ── Why this is non-trivial ───────────────────────────────────────────────
 *
 * SMPL pose values are axis-angle rotations expressed in each joint's
 * **rest local frame**, which by SMPL convention equals the WORLD frame
 * (every joint's rest local rotation is identity). So a "+Z rotation"
 * in SMPL means "rotate around world Z."
 *
 * Mixamo bones, by contrast, have non-trivial bind-local rotations —
 * each bone is rotated relative to its parent so its local +Y axis
 * points along the bone toward the child. For a Mixamo upper-arm bone
 * that means local Z is roughly aligned with world Y, so dumping a
 * "+Z rotation" straight onto `bone.rotation.z` rotates the arm
 * horizontally rather than vertically. (This is exactly the bug the
 * naive version of this module had: a "hands up to head" SMPL pose
 * came out as "arms forward like a zombie" on the Mixamo character.)
 *
 * The fix is a forward-kinematic retargeting pass:
 *
 *   1. Walk the SMPL kinematic chain (parent before child).
 *   2. Compute each joint's **target world rotation** by composing
 *      `parent_world × q_smpl_local`. SMPL rest = identity makes this
 *      a clean recursion.
 *   3. For each joint that maps to a Mixamo `BoneSlot`, look up the
 *      Mixamo parent's already-computed world rotation and back out the
 *      bone-local rotation that produces the target world:
 *          bone.quaternion = parent_mixamo_world⁻¹ × target_world
 *      Record this as the new world rotation for the slot, so
 *      descendant SMPL joints whose Mixamo parents differ from their
 *      SMPL parents (rare but it happens — e.g. Mixamo collapses
 *      `neck` into `Spine2 → Clavicle` whereas SMPL routes shoulders
 *      via `spine3 → collar`) compose against the correct chain.
 *   4. Decompose the resulting bone-local rotation to Euler XYZ and
 *      subtract the bone's own bind Euler — the procedural-hold layer
 *      stores deltas-from-bind, not absolute rotations.
 *
 * The bind quaternions per slot come from `figure.getBindRotations()`,
 * which snapshots them at character load.
 *
 * ── References ────────────────────────────────────────────────────────────
 *
 * • SMPL: https://smpl.is.tue.mpg.de/
 * • SMPL joint hierarchy: github.com/vchoutas/smplx (vertices.py)
 * • PoseScript dataset: github.com/naver/posescript
 */

import { Euler, Quaternion, Vector3 } from 'three';
import type { BonePose, BoneSlot, EulerOffset } from './body-profiles';

// ── SMPL joint table ──────────────────────────────────────────────────────

/**
 * Standard SMPL joint names by index. Order matches `vchoutas/smplx`'s
 * `JOINT_NAMES` constant and the layout used in every downstream pose
 * dataset (AMASS, PoseScript, BABEL, HumanML3D).
 */
export const SMPL_JOINT_NAMES = [
  /*  0 */ 'pelvis',
  /*  1 */ 'left_hip',
  /*  2 */ 'right_hip',
  /*  3 */ 'spine1',
  /*  4 */ 'left_knee',
  /*  5 */ 'right_knee',
  /*  6 */ 'spine2',
  /*  7 */ 'left_ankle',
  /*  8 */ 'right_ankle',
  /*  9 */ 'spine3',
  /* 10 */ 'left_foot',
  /* 11 */ 'right_foot',
  /* 12 */ 'neck',
  /* 13 */ 'left_collar',
  /* 14 */ 'right_collar',
  /* 15 */ 'head',
  /* 16 */ 'left_shoulder',
  /* 17 */ 'right_shoulder',
  /* 18 */ 'left_elbow',
  /* 19 */ 'right_elbow',
  /* 20 */ 'left_wrist',
  /* 21 */ 'right_wrist',
  /* 22 */ 'left_hand',
  /* 23 */ 'right_hand',
] as const;

export type SmplJointName = (typeof SMPL_JOINT_NAMES)[number];

/**
 * Parent index per SMPL joint. -1 marks the root (pelvis). The joint
 * order is naturally topological (every parent has a smaller index
 * than its children), so a single forward pass `i = 0..23` is a valid
 * forward-kinematic traversal.
 */
export const SMPL_PARENT: readonly number[] = [
  /*  0 pelvis        */ -1,
  /*  1 left_hip      */ 0,
  /*  2 right_hip     */ 0,
  /*  3 spine1        */ 0,
  /*  4 left_knee     */ 1,
  /*  5 right_knee    */ 2,
  /*  6 spine2        */ 3,
  /*  7 left_ankle    */ 4,
  /*  8 right_ankle   */ 5,
  /*  9 spine3        */ 6,
  /* 10 left_foot     */ 7,
  /* 11 right_foot    */ 8,
  /* 12 neck          */ 9,
  /* 13 left_collar   */ 9,
  /* 14 right_collar  */ 9,
  /* 15 head          */ 12,
  /* 16 left_shoulder */ 13,
  /* 17 right_shoulder*/ 14,
  /* 18 left_elbow    */ 16,
  /* 19 right_elbow   */ 17,
  /* 20 left_wrist    */ 18,
  /* 21 right_wrist   */ 19,
  /* 22 left_hand     */ 20,
  /* 23 right_hand    */ 21,
];

/**
 * SMPL joint index → our procedural `BoneSlot` for the body bones we
 * actually drive in the procedural-hold layer.
 *
 * Joints we deliberately drop:
 *   • 0 pelvis     — handled by global orientation; never applied as an
 *                    offset (would rotate the whole character away from
 *                    the camera). Still walked for FK so child world
 *                    rotations compose correctly.
 *   • 10/11 toes   — Mixamo's `LeftFoot`/`RightFoot` are the ankles in
 *                    our slot vocabulary; toe joints aren't part of the
 *                    procedural-hold offset surface.
 *   • 15 head      — gaze layer drives the head bone; we don't want
 *                    SMPL retargeting to fight it.
 *   • 22/23 hands  — collapsed finger chains; SMPL doesn't carry per-
 *                    finger detail and our rig has separate finger
 *                    bones outside the slot vocabulary.
 */
export const SMPL_TO_BONE_SLOT: Partial<Record<number, BoneSlot>> = {
  1:  'upLegL',
  2:  'upLegR',
  3:  'spine',     // SMPL spine1 → Mixamo spine
  4:  'legL',
  5:  'legR',
  6:  'spine1',    // SMPL spine2 → Mixamo spine1
  7:  'footL',
  8:  'footR',
  9:  'spine2',    // SMPL spine3 → Mixamo spine2
  12: 'neck',
  13: 'clavicleL',
  14: 'clavicleR',
  16: 'upperArmL',
  17: 'upperArmR',
  18: 'forearmL',
  19: 'forearmR',
  20: 'handL',
  21: 'handR',
};

/**
 * Mixamo bone-slot parent map. Note where this differs from SMPL's:
 *   • Mixamo's clavicles parent off `Spine2` (= our `spine2`), not
 *     a sub-neck joint. SMPL puts collars off `spine3` (which is also
 *     our `spine2` — the trailing spine bone). Topology matches.
 *   • Mixamo's neck → head chain mirrors SMPL.
 *   • Hips and shoulders are well-aligned; no surprises.
 *
 * `null` marks the root (`hips`). Slots not present here have no Mixamo
 * parent in our vocabulary and are skipped by the retargeter.
 */
export const MIXAMO_PARENT_SLOT: Partial<Record<BoneSlot, BoneSlot | null>> = {
  hips:       null,
  spine:      'hips',
  spine1:     'spine',
  spine2:     'spine1',
  neck:       'spine2',
  clavicleL:  'spine2',
  clavicleR:  'spine2',
  upperArmL:  'clavicleL',
  upperArmR:  'clavicleR',
  forearmL:   'upperArmL',
  forearmR:   'upperArmR',
  handL:      'forearmL',
  handR:      'forearmR',
  upLegL:     'hips',
  upLegR:     'hips',
  legL:       'upLegL',
  legR:       'upLegR',
  footL:      'legL',
  footR:      'legR',
};

/**
 * Topological order over Mixamo slots — every slot appears AFTER its
 * parent. Used for the bind-FK pre-pass so we visit `hips` before any
 * descendant whose parent chain passes through it.
 */
export const MIXAMO_TOPO_ORDER: readonly BoneSlot[] = [
  'hips',
  'spine', 'spine1', 'spine2',
  'neck',
  'clavicleL', 'upperArmL', 'forearmL', 'handL',
  'clavicleR', 'upperArmR', 'forearmR', 'handR',
  'upLegL', 'legL', 'footL',
  'upLegR', 'legR', 'footR',
];

// ── Bind-rotation type ────────────────────────────────────────────────────

export interface BindRotationEntry {
  qx: number; qy: number; qz: number; qw: number;
  ex: number; ey: number; ez: number;
}

export type BindRotationsBySlot = Partial<Record<BoneSlot, BindRotationEntry>>;

// ── Tunables ──────────────────────────────────────────────────────────────

/**
 * Drop-axis filter: axes where |delta| is below this radian threshold
 * are omitted from the BonePose entry. Matches what `extract-pose.ts`
 * uses for clip-derived poses, so the on-disk representation is
 * stylistically consistent.
 */
const OFFSET_DEAD_BAND_RAD = 0.01;

// ── Math helpers ──────────────────────────────────────────────────────────

const _q = new Quaternion();
const _qParent = new Quaternion();
const _qLocal = new Quaternion();
const _qBind = new Quaternion();
const _axis = new Vector3();
const _eulerPose = new Euler(0, 0, 0, 'XYZ');
const _eulerBind = new Euler(0, 0, 0, 'XYZ');

function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}

/**
 * Wrap a delta to (-π, π]. Three.js's Euler XYZ extraction returns y/z
 * in [-π, π]; if the bind decomposition is near +π and the pose is near
 * -π, the raw subtraction would give a ~2π delta when the actual
 * rotation difference is tiny. Wrap before storing.
 */
function wrapDelta(d: number): number {
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return d;
}

/** Build a quaternion from an axis-angle triple, in-place into `out`. */
function setQuaternionFromAxisAngle(
  out: Quaternion,
  rx: number,
  ry: number,
  rz: number,
): void {
  const angle = Math.hypot(rx, ry, rz);
  if (angle < 1e-8) {
    out.set(0, 0, 0, 1);
    return;
  }
  _axis.set(rx / angle, ry / angle, rz / angle);
  out.setFromAxisAngle(_axis, angle);
}

// ── Retargeting ───────────────────────────────────────────────────────────

/**
 * Convert a flat SMPL pose vector (length 72) to a `BonePose` for our
 * Mixamo character.
 *
 * `binds` is the per-slot bind-local rotation table from
 * `figure.getBindRotations()`. **Required** for correct results — when
 * absent, we fall back to a "naive" pass that applies SMPL rotations
 * directly to bone-local axes, which is wrong for Mixamo's non-trivial
 * bind orientations and exists only as a degraded fallback for callers
 * that haven't wired the figure through yet.
 *
 * Throws when `pose.length < 72`.
 */
export function smplPoseToBonePose(
  pose: ArrayLike<number>,
  binds?: BindRotationsBySlot,
  opts?: { debug?: boolean; label?: string },
): BonePose {
  if (pose.length < 72) {
    throw new Error(
      `smplPoseToBonePose: pose has length ${pose.length}, expected >= 72`,
    );
  }
  const result = binds ? retargetWithFK(pose, binds) : retargetNaive(pose);
  if (opts?.debug) {
    logRetargetDebug(pose, binds, result, opts.label ?? '(unlabelled)');
  }
  return result;
}

/**
 * Print a compact diagnostic dump for a single retarget call. Includes:
 *   • the raw SMPL pose for every joint we drive (so we can verify the
 *     seed JSON wasn't mangled in transit),
 *   • the bind euler we read off the live character (so we can compare
 *     against the synthetic bind used in smoke tests),
 *   • the resulting BonePose offsets per slot.
 *
 * Call site is the tuning-panel result-apply, gated behind the panel's
 * debug toggle, so this runs on user demand only.
 */
function logRetargetDebug(
  pose: ArrayLike<number>,
  binds: BindRotationsBySlot | undefined,
  out: BonePose,
  label: string,
): void {
  const lines: string[] = [];
  lines.push(`[smpl-retarget] ${label}`);

  // First — dump the entire bind chain so we can see what coordinate
  // frame the math is operating against. This is the data my hand-derived
  // math depends on; if (e.g.) the clavicles have non-trivial rotation,
  // it changes the basis the SMPL pose value is conjugated through.
  if (binds) {
    lines.push('  BIND CHAIN (Mixamo bone-local Euler XYZ at rest):');
    for (const slot of MIXAMO_TOPO_ORDER) {
      const b = binds[slot];
      if (!b) continue;
      const big = Math.max(Math.abs(b.ex), Math.abs(b.ey), Math.abs(b.ez)) > 0.05;
      if (!big) continue; // skip near-identity bones
      lines.push(
        `    ${slot.padEnd(10)} ex=${b.ex.toFixed(3)} ey=${b.ey.toFixed(3)} ez=${b.ez.toFixed(3)}` +
        `   q=(${b.qx.toFixed(3)},${b.qy.toFixed(3)},${b.qz.toFixed(3)},${b.qw.toFixed(3)})`,
      );
    }
  }

  // Per-joint retarget result.
  for (let i = 0; i < 24; i += 1) {
    const slot = SMPL_TO_BONE_SLOT[i];
    if (!slot) continue;
    const rx = pose[i * 3];
    const ry = pose[i * 3 + 1];
    const rz = pose[i * 3 + 2];
    const mag = Math.hypot(rx, ry, rz);
    if (mag < 1e-4 && !out[slot]) continue;
    const bind = binds?.[slot];
    const bindStr = bind
      ? `bind=(${bind.ex.toFixed(2)},${bind.ey.toFixed(2)},${bind.ez.toFixed(2)})`
      : 'bind=?';
    const off = out[slot];
    const offStr = off ? `off=${JSON.stringify(off)}` : 'off=∅';
    const smplName = SMPL_JOINT_NAMES[i];
    lines.push(
      `  ${smplName.padEnd(16)} → ${slot.padEnd(10)} ` +
      `smpl=(${rx.toFixed(2)},${ry.toFixed(2)},${rz.toFixed(2)}) ` +
      `${bindStr} ${offStr}`,
    );
  }
  // eslint-disable-next-line no-console
  console.log(lines.join('\n'));
}

/**
 * Forward-kinematic retargeting.
 *
 * The math (derivation in module header):
 *
 *   q_pose_local = R_p_bind_world⁻¹ × q_smpl_local × R_p_bind_world × q_bind_local
 *
 * where:
 *   R_p_bind_world = parent bone's BIND world rotation (one-time FK on
 *                    Mixamo bind locals)
 *   q_smpl_local   = the SMPL pose value for this joint (axis-angle)
 *   q_bind_local   = this bone's bind local rotation
 *
 * The similarity transform `R_p⁻¹ × q × R_p` is a basis change — it
 * rotates the SMPL pose value (which lives in SMPL's world frame, since
 * SMPL rest local = identity for every joint) into the parent's bind-
 * world frame, which is the frame Mixamo's bone-local quaternion is
 * expressed in. Composing with the bind local gives the bone's full
 * pose-local quaternion, which we then decompose against the bind to
 * get the Euler offset our additive layer expects.
 *
 * The recursion is implicit: each bone's offset is computed
 * independently, but the math guarantees that when ALL bones are
 * offset by their respective values, the world rotation of every bone
 * equals `q_smpl_world × R_b_bind_world` — i.e. the SMPL world delta
 * is layered on top of the Mixamo bind. This is exactly what we want
 * for the procedural-hold layer's "delta from rest" semantics.
 *
 * SMPL=identity therefore produces a zero offset (q_smpl_local = I,
 * the similarity transform vanishes, q_pose_local = q_bind_local,
 * delta = 0). The previous implementation forced absolute world
 * equality (q_pose_world = q_smpl_world) which incorrectly tried to
 * "un-rotate" the bind, producing huge spurious offsets at SMPL=I.
 */
function retargetWithFK(
  pose: ArrayLike<number>,
  binds: BindRotationsBySlot,
): BonePose {
  const out: BonePose = {};

  // ── One-time FK on Mixamo bind chain ───────────────────────────────────
  //
  // For each slot we drive, compute the bone's bind-world rotation (the
  // accumulated product of bind-local rotations from root to this bone).
  // We only need PARENT bind world when retargeting each bone, but
  // computing them all in one pass and looking them up by parent slot
  // is simpler than threading them through the per-joint loop.
  //
  // CRITICAL: iterate over `MIXAMO_TOPO_ORDER`, not SMPL indices. Hips
  // is the Mixamo root and is the parent of every other bone's chain,
  // but pelvis (SMPL idx 0) maps to no slot — if we iterated SMPL we'd
  // skip hips and `bindWorld['hips']` would never get populated, leaving
  // every descendant's parent-bind-world defaulted to identity. That
  // produced the visible asymmetric arm result on real (non-identity-
  // hipped) characters: bones whose parent chain passed through a non-
  // identity bind got the wrong basis for their similarity transform.
  const bindWorld: Partial<Record<BoneSlot, Quaternion>> = {};
  for (const slot of MIXAMO_TOPO_ORDER) {
    const bind = binds[slot];
    if (!bind) continue;
    const parentSlot = MIXAMO_PARENT_SLOT[slot];
    const parentBindWorld = parentSlot && bindWorld[parentSlot]
      ? bindWorld[parentSlot]!
      : null;
    const localBind = new Quaternion(bind.qx, bind.qy, bind.qz, bind.qw);
    if (parentBindWorld) {
      bindWorld[slot] = parentBindWorld.clone().multiply(localBind);
    } else {
      bindWorld[slot] = localBind;
    }
  }

  // ── Per-joint retargeting ──────────────────────────────────────────────

  for (let i = 0; i < 24; i += 1) {
    const slot = SMPL_TO_BONE_SLOT[i];
    if (!slot) continue;

    const bind = binds[slot];
    if (!bind) {
      // Slot in our vocabulary but absent on this rig. Naive fallback
      // so the user still sees a (degraded) result rather than nothing.
      const e = naiveAxisAngleToEuler(pose[i * 3], pose[i * 3 + 1], pose[i * 3 + 2]);
      if (e) out[slot] = e;
      continue;
    }

    // Step 1: SMPL pose value for this joint. Pelvis is force-zeroed so
    // the global character orientation in the source PoseScript pose
    // doesn't tilt our character (holds are torso-relative).
    setQuaternionFromAxisAngle(
      _qLocal,
      i === 0 ? 0 : pose[i * 3],
      i === 0 ? 0 : pose[i * 3 + 1],
      i === 0 ? 0 : pose[i * 3 + 2],
    );

    // Step 2: parent's bind world rotation, in which q_smpl_local will
    // be re-expressed via similarity transform.
    const parentSlot = MIXAMO_PARENT_SLOT[slot];
    if (parentSlot && bindWorld[parentSlot]) {
      _qParent.copy(bindWorld[parentSlot]!);
    } else {
      _qParent.set(0, 0, 0, 1);
    }

    // Step 3: similarity transform — express q_smpl_local in parent's
    // bind frame.
    //   inParentFrame = parent⁻¹ × q_smpl_local × parent
    // Reusing _q to hold the result.
    _q.copy(_qParent).invert().multiply(_qLocal).multiply(_qParent);

    // Step 4: compose with bind to get pose local.
    //   q_pose_local = inParentFrame × bind_local
    _qBind.set(bind.qx, bind.qy, bind.qz, bind.qw);
    _q.multiply(_qBind);

    // Step 5: Euler delta from bind. Wrap each axis to (-π, π] in case
    // the decomposition crosses the ±π boundary.
    _eulerPose.setFromQuaternion(_q, 'XYZ');
    _eulerBind.setFromQuaternion(_qBind, 'XYZ');

    const dx = wrapDelta(_eulerPose.x - _eulerBind.x);
    const dy = wrapDelta(_eulerPose.y - _eulerBind.y);
    const dz = wrapDelta(_eulerPose.z - _eulerBind.z);

    const offset: EulerOffset = {};
    if (Math.abs(dx) > OFFSET_DEAD_BAND_RAD) offset.x = round3(dx);
    if (Math.abs(dy) > OFFSET_DEAD_BAND_RAD) offset.y = round3(dy);
    if (Math.abs(dz) > OFFSET_DEAD_BAND_RAD) offset.z = round3(dz);
    if (Object.keys(offset).length > 0) out[slot] = offset;
  }

  return out;
}

/**
 * Single-axis-angle → Euler XYZ. The "naive" path: ignores the bone's
 * bind orientation entirely, producing wrong-axis rotations on Mixamo
 * rigs whose bones aren't axis-aligned. Kept only as a degraded
 * fallback when we don't have bind rotations available.
 */
function naiveAxisAngleToEuler(
  rx: number,
  ry: number,
  rz: number,
): EulerOffset | null {
  setQuaternionFromAxisAngle(_q, rx, ry, rz);
  if (_q.w > 0.99999) return null; // identity
  _eulerPose.setFromQuaternion(_q, 'XYZ');
  const offset: EulerOffset = {};
  if (Math.abs(_eulerPose.x) > OFFSET_DEAD_BAND_RAD) offset.x = round3(_eulerPose.x);
  if (Math.abs(_eulerPose.y) > OFFSET_DEAD_BAND_RAD) offset.y = round3(_eulerPose.y);
  if (Math.abs(_eulerPose.z) > OFFSET_DEAD_BAND_RAD) offset.z = round3(_eulerPose.z);
  if (Object.keys(offset).length === 0) return null;
  return offset;
}

function retargetNaive(pose: ArrayLike<number>): BonePose {
  const out: BonePose = {};
  for (let smplIdx = 0; smplIdx < 24; smplIdx += 1) {
    const slot = SMPL_TO_BONE_SLOT[smplIdx];
    if (!slot) continue;
    const offset = naiveAxisAngleToEuler(
      pose[smplIdx * 3],
      pose[smplIdx * 3 + 1],
      pose[smplIdx * 3 + 2],
    );
    if (offset) out[slot] = offset;
  }
  return out;
}

// ── Utility — accept arbitrary server payload shapes ──────────────────────

/**
 * Parse a JSON-or-array SMPL pose received from the server. Used by the
 * tuning-panel POSE_SEARCH path. Accepts:
 *   • a 72-length number[]                            — direct
 *   • { smpl_pose: number[] }                         — server payload
 *   • { pose: number[] } | { pose_body: number[] }    — alternate names
 *
 * Throws on shapes we don't recognise.
 */
export function coerceSmplPose(input: unknown): number[] {
  if (Array.isArray(input)) {
    if (input.length >= 72) return input as number[];
    throw new Error(`SMPL pose array too short: ${input.length} < 72`);
  }
  if (input && typeof input === 'object') {
    const obj = input as Record<string, unknown>;
    for (const key of ['smpl_pose', 'pose', 'pose_body', 'body_pose']) {
      const v = obj[key];
      if (Array.isArray(v) && v.length >= 72) return v as number[];
    }
  }
  throw new Error('SMPL pose: unrecognised input shape');
}
